import React, { useState, useEffect } from "react";
import TrainingAPI from "../../services/TrainingAPI";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "../css/TodaysFocusCard.css";

/**
 * TodaysFocusCard Component
 *
 * Surfaces exactly ONE action — the most important thing for the user right now.
 * Designed for 50+ audience: clear, simple, and action-oriented.
 */
function TodaysFocusCard({ setHeroContent }) {
  const { t } = useTranslation();
  const [focusAction, setFocusAction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    determineFocusAction();
  }, []);

  const determineFocusAction = async () => {
    try {
      // Try to get training progress to determine next action
      const [progressRes, trainingsRes] = await Promise.all([
        TrainingAPI.getUserProgress().catch(() => null),
        TrainingAPI.getAllTrainings().catch(() => null),
      ]);

      // Find the next incomplete training
      let nextTraining = null;
      if (trainingsRes?.success && trainingsRes.trainings) {
        nextTraining = trainingsRes.trainings.find(
          (t) => t.userStatus !== "COMPLETED",
        );
      }

      // Derive counts from trainings array for accuracy; fall back to progress API
      const trainings = trainingsRes?.trainings || [];
      const completedFromTrainings = trainings.filter(
        (tr) => tr.userStatus === "COMPLETED",
      ).length;
      const completedCount =
        completedFromTrainings > 0
          ? completedFromTrainings
          : progressRes?.progress?.completedTrainings || 0;
      const totalCount =
        trainings.length > 0
          ? trainings.length
          : progressRes?.progress?.totalTrainings || 0;

      if (nextTraining) {
        setFocusAction({
          type: "training",
          title:
            nextTraining.title || t("dashboard.todaysFocus.nextSafetyLesson"),
          subtitle: t("dashboard.todaysFocus.trainingOf")
            .replace("{completed}", completedCount + 1)
            .replace("{total}", totalCount),
          buttonText: t("dashboard.todaysFocus.startNow"),
          icon: "🎯",
        });
      } else if (completedCount === 0) {
        setFocusAction({
          type: "getting-started",
          title: t("dashboard.todaysFocus.startFirstTitle"),
          subtitle: t("dashboard.todaysFocus.startFirstSub"),
          buttonText: t("dashboard.todaysFocus.getStarted"),
          icon: "🎯",
        });
      } else if (completedCount >= totalCount && totalCount > 0) {
        setFocusAction({
          type: "all-complete",
          title: t("dashboard.todaysFocus.allCompleteTitle"),
          subtitle: t("dashboard.todaysFocus.allCompleteSub"),
          buttonText: t("dashboard.todaysFocus.viewProgress"),
          icon: "🏆",
        });
      } else {
        setFocusAction({
          type: "continue",
          title: t("dashboard.todaysFocus.continueTitle"),
          subtitle: t("dashboard.todaysFocus.completedOf")
            .replace("{completed}", completedCount)
            .replace("{total}", totalCount),
          buttonText: t("dashboard.todaysFocus.continueBtn"),
          icon: "🎯",
        });
      }
    } catch (err) {
      console.error("[TodaysFocusCard] Error:", err);
      setFocusAction({
        type: "default",
        title: t("dashboard.todaysFocus.buildSkillsTitle"),
        subtitle: t("dashboard.todaysFocus.buildSkillsSub"),
        buttonText: t("dashboard.todaysFocus.getStarted"),
        icon: "🎯",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = () => {
    if (setHeroContent) {
      setHeroContent("Training Center");
    }
  };

  if (loading || !focusAction) {
    return null; // Don't show a loading state — just appear when ready
  }

  return (
    <div className="todays-focus-card">
      <div className="todays-focus-content">
        <span className="todays-focus-label">
          {t("dashboard.todaysFocus.label")}
        </span>
        <span className="todays-focus-title">{focusAction.title}</span>
        <span className="todays-focus-subtitle">{focusAction.subtitle}</span>
        <button className="todays-focus-button" onClick={handleAction}>
          {focusAction.buttonText}
        </button>
      </div>
    </div>
  );
}

export default TodaysFocusCard;
